import React, { ChangeEvent, FormEvent } from "react";
import {
  asyncScheduler,
  from,
  Observable,
  of,
  Subject,
  Subscription,
} from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  throttleTime,
} from "rxjs/operators";
import ConfigProps from "../../constants/configProps";
import { IDeptartment } from "../../interfaces/IDepartment";
import { IDisplayState } from "../../interfaces/IDisplayState";
import AnalyticsService from "../../utilities/analyticsService";
import ConfigPropService from "../../utilities/configPropService";
import DigitalDataService from "../../utilities/digitalDataService";
import EnvironmentService from "../../utilities/environmentService";
import QueryStringService from "../../utilities/queryStringService";
import RecentSearchesService from "../../utilities/recentSearchesService";
import ServerSideService from "../../utilities/serverSideService";
import SessionCacheService from "../../utilities/sessionCacheService";
import { TabSetsService } from "../../utilities/tabSetsService";
import { TypeAheadService } from "../../utilities/typeAheadService";
import {
  getCurrentSearchSuggestion,
  setCurrentSearchSuggestion,
} from "../../utilities/utilities";
import DepartmentSelect from "./departmentSelect/departmentSelect";
import SearchButton from "./searchButton";
import SearchClear from "./searchClear";
import "./searchComponent.scss";
import SearchInput from "./searchInput";
import SuggestionDropdown from "./suggestionDropdown";

const AllDepartments = {
  name: "All Departments",
  value: "",
};

export interface ISearchComponentProps {
  displayState: IDisplayState;
  isMobile?: boolean;
  searchKeyword: string;
  configProps?: Record<string, string>;
  onSearchKeywordChange: (value: string) => void;
  forceCloseDep?: boolean;
  searchAction?: string;
}

export interface ISearchComponentState {
  departments: { name: string; value: string; searchCategoryId?: string }[];
  suggestions: { isRecent: boolean; value: string }[];
  hasSearchInput: boolean;
  hasSearchFocus: boolean;
  hoveredSuggestion: number;
  hasSuggestionMouse: boolean;
  department: string;
  departmentSelectWidth: number;
  suggestionSelected: boolean;
  hasSearchClearMouse: boolean;
  deletedLeastRecentSearch: boolean;
  isEcom: boolean;
  shopUrl: string;
}

class SearchComponent extends React.Component<
  ISearchComponentProps,
  ISearchComponentState
> {
  suggestionsSubject: Subject<string>;
  suggestionNavigationSubject: Subject<number>;
  suggestionNavigationObservable: Observable<number>;

  departmentsSub: Subscription | undefined;
  suggestionsSub: Subscription | undefined;

  constructor(props: ISearchComponentProps) {
    super(props);

    let departmentId =
      ServerSideService.isServerSide &&
      this.props.displayState.departmentCategoryId
        ? this.props.displayState.departmentCategoryId
        : QueryStringService.getParam("Category") || "";

    if (this.props.isMobile) {
      departmentId = "";
    }

    let departments = [AllDepartments];

    if (departmentId && this.props.displayState.departmentName) {
      departments = [
        AllDepartments,
        {
          name: this.props.displayState.departmentName,
          value: departmentId,
        },
      ];
    }

    this.state = {
      departments: departments,
      suggestions: [],
      hasSearchInput: this.props.searchKeyword !== "",
      hasSearchFocus: false,
      hoveredSuggestion: -1,
      hasSuggestionMouse: false,
      department: departmentId,
      departmentSelectWidth: 136,
      suggestionSelected: false,
      hasSearchClearMouse: false,
      deletedLeastRecentSearch: false,
      isEcom: EnvironmentService.get().isEcom === true,
      shopUrl: "/shop"
    };

    this.suggestionsSubject = new Subject();
    this.suggestionNavigationSubject = new Subject();
    this.suggestionNavigationObservable =
      this.suggestionNavigationSubject.asObservable();

    this.onSearchChange = this.onSearchChange.bind(this);
    this.onSuggestionSelect = this.onSuggestionSelect.bind(this);
    this.onSearchFocus = this.onSearchFocus.bind(this);
    this.onSuggestionNavigate = this.onSuggestionNavigate.bind(this);
    this.onSuggestionHover = this.onSuggestionHover.bind(this);
    this.suggestionMouseEnter = this.suggestionMouseEnter.bind(this);
    this.suggestionMouseLeave = this.suggestionMouseLeave.bind(this);
    this.onSubmitHandler = this.onSubmitHandler.bind(this);
    this.onDepartmentChange = this.onDepartmentChange.bind(this);
    this.onSearchClear = this.onSearchClear.bind(this);
    this.searchClearMouseEnter = this.searchClearMouseEnter.bind(this);
    this.searchClearMouseLeave = this.searchClearMouseLeave.bind(this);
    this.removeRecentSuggestion = this.removeRecentSuggestion.bind(this);
    this.clickedOutsideOfDropdown = this.clickedOutsideOfDropdown.bind(this);
  }

  componentWillUnmount() {
    if (typeof document !== undefined) {
      document.removeEventListener(
        "closeSearchBarDropdown",
        this.clickedOutsideOfDropdown
      );
    }

    if (this.departmentsSub) {
      this.departmentsSub.unsubscribe();
    }

    if (this.suggestionsSub) {
      this.suggestionsSub.unsubscribe();
    }
  }

  componentDidMount() {
    if (typeof document !== undefined) {
      document.addEventListener(
        "closeSearchBarDropdown",
        this.clickedOutsideOfDropdown
      );

      const isLocal = window.location.hostname === "localhost";
      const isDev =
        EnvironmentService.get().ecomSearch!.indexOf("wbmasondev.com") > 0;

      if (isDev && !isLocal) {
        this.setState({
          shopUrl: EnvironmentService.get().ecomSearch!,
        });
      }
    }

    if (!this.props.isMobile) {
      this.departmentsSub = SessionCacheService.get(
        "Department",
        TabSetsService.getDepartments(),
        (departments: IDeptartment[]) => {
          departments = departments ? departments : [];
          departments.unshift(AllDepartments);

          const matchedDepartment = departments.filter((x) => {
            return x.searchCategoryId === this.state.department;
          });

          this.setState(
            {
              departments: departments,
              department:
                matchedDepartment && matchedDepartment.length !== 0
                  ? this.state.department
                  : "",
            },
            () => {
              this.onDepartmentChange({
                target: {
                  value: this.state.department,
                },
              } as ChangeEvent<HTMLSelectElement>);
            }
          );
        }
      );
    }

    this.suggestionsSub = (
      this.suggestionsSubject
        .asObservable()
        .pipe(
          throttleTime(500, asyncScheduler, { leading: false, trailing: true })
        )
        .pipe(distinctUntilChanged())
        .pipe(
          switchMap((value: string) => {
            return value
              ? TypeAheadService.getTypeAhead(value)
                  .pipe(
                    catchError(() => {
                      return of({});
                    })
                  )
                  .pipe(
                    map((response: any) =>
                      response?.clientCache?.results
                        ? response?.clientCache?.results.map(
                            (r: { text: string }) => {
                              return {
                                value: r.text,
                                isRecent: false,
                              };
                            }
                          )
                        : []
                    )
                  )
              : from([]);
          })
        ) as Observable<{ isRecent: boolean; value: string }[]>
    ).subscribe((suggestions) => {
      this.setState({
        suggestions: this.filterSuggestions(
          suggestions,
          this.props.searchKeyword
        ),
      });
    });

    if (this.state.departments.length > 1) {
      this.onDepartmentChange({
        target: {
          value: this.state.department,
        },
      } as ChangeEvent<HTMLSelectElement>);
    }
  }

  filterSuggestions(
    suggestions: { isRecent: boolean; value: string; html?: React.ReactNode }[],
    search: string
  ) {
    suggestions = suggestions.filter((suggestion) => {
      return (
        suggestion.isRecent === false &&
        this.props.configProps &&
        ConfigPropService.isYesOrTrue(
          this.props.configProps[ConfigProps.EnableTypeAhead]
        ) &&
        (suggestion.value.toLowerCase().indexOf(search.toLowerCase()) === 0 ||
          suggestion.value.toLowerCase().indexOf(" " + search.toLowerCase()) !== -1)
      );
    });

    let recentSearches = RecentSearchesService.get();

    if (recentSearches && !Array.isArray(recentSearches)) {
      recentSearches = [];
      RecentSearchesService.set(recentSearches);
    }

    if (
      recentSearches &&
      Array.isArray(recentSearches) &&
      this.props.configProps &&
      ConfigPropService.isYesOrTrue(
        this.props.configProps[ConfigProps.SaveUserSearches]
      )
    ) {
      const matches = recentSearches
        .filter((suggestion: string) => {
          return (
            !search ||
            suggestion.indexOf(search) === 0 ||
            suggestion.indexOf(" " + search) !== -1
          );
        })
        .map((suggestion: string) => {
          return {
            value: suggestion,
            isRecent: true,
          };
        });

      for (let i = 0; i < matches.length; i++) {
        suggestions = suggestions.filter((suggestion) => {
          return suggestion.value !== matches[i].value;
        });

        suggestions.unshift(matches[i]);
      }
    }

    suggestions = suggestions.filter((suggestion, index) => index <= 5);

    suggestions.forEach((suggestion) => {
      const match = " " + search.trim().toLowerCase();
      let tokens = " " + suggestion.value.toLowerCase();
      let index = 0;
      const display: string[] = [];

      while ((index = tokens.indexOf(match)) !== -1) {
        if (index === 0) {
          tokens = tokens.substring(match.length);
          display.push(match);
        } else {
          const lhs = tokens.substring(0, index);
          tokens = tokens.substring(index + match.length);
          display.push(lhs);
          display.push(match);
        }
      }

      if (tokens.length !== 0) {
        display.push(tokens);
      }

      suggestion.html = (
        <span>
          {display.map((value, index) => {
            return (
              <span
                key={index}
                style={value === match ? { fontFamily: "PT Sans Narrow Bold" } : {}}
              >
                {index === 0 ? value.substring(1) : value}
              </span>
            );
          })}
        </span>
      );
    });

    return suggestions;
  }

  onSearchChange(value: string) {
    if (
      this.props.configProps &&
      ConfigPropService.isYesOrTrue(
        this.props.configProps[ConfigProps.EnableTypeAhead]
      )
    ) {
      this.suggestionsSubject.next(value);
    }

    this.props.onSearchKeywordChange(value);

    this.setState({
      hasSearchInput: !!value && value.length !== 0,
      hasSearchFocus: true,
      suggestions: this.filterSuggestions(this.state.suggestions, value),
    });
  }

  onSearchFocus(value: boolean) {
    if (this.state.hasSearchFocus !== value) {
      const newState: Partial<ISearchComponentState> = {
        hasSearchFocus: value,
      };

      if (value && !this.props.searchKeyword) {
        newState.suggestions = this.filterSuggestions(
          [],
          this.props.searchKeyword
        );
      }

      this.setState(newState as ISearchComponentState);
    }
  }

  onSuggestionSelect(value: number) {
    setCurrentSearchSuggestion(this.state.suggestions[value].value);
    this.setState({
      suggestionSelected: true,
    });
    this.onSearchChange(this.state.suggestions[value].value);
  }

  onSuggestionNavigate(value: number) {
    this.suggestionNavigationSubject.next(value);
  }

  onSuggestionHover(value: number) {
    this.setState({
      hoveredSuggestion: value,
    });
  }

  suggestionMouseLeave() {
    this.setState({
      hasSuggestionMouse: false,
    });

    if (this.state.deletedLeastRecentSearch) {
      this.setState({
        hasSuggestionMouse: true,
      });
    }
  }

  suggestionMouseEnter() {
    this.setState({
      hasSuggestionMouse: true,
    });
  }

  onSubmitHandler(event: FormEvent<HTMLFormElement>) {
    const search = this.props.searchKeyword
      ? this.props.searchKeyword.replace(/\s\s+/g, " ").trim()
      : "";

    let isCategorySearch = false;
    let isKeywordSearch = false;
    const typeAheadResultSelected =
      this.state.suggestionSelected ||
      (this.props.searchKeyword &&
        getCurrentSearchSuggestion() === this.props.searchKeyword);

    if (!this.state.department && !search) {
      event.preventDefault();
      return;
    }

    if (search !== this.props.searchKeyword) {
      this.props.onSearchKeywordChange(search);
    }

    // added hidden category id to ensure the category id is appended as the last member of the query string
    const categoryElement = document.getElementsByName("Category")[0];
    const fakeCategoryElement = document.getElementById("Category");
    const hiddenCategoryElement = document.getElementById("HiddenCategoryId");
    if (fakeCategoryElement) {
      fakeCategoryElement.setAttribute("name", "");
      fakeCategoryElement.setAttribute("id", "");
    }
    if (categoryElement) {
      categoryElement.setAttribute("name", "");
      categoryElement.setAttribute("id", "");
      categoryElement.setAttribute("value", "");
    }

    if (hiddenCategoryElement) {
      hiddenCategoryElement.setAttribute("name", "");
      hiddenCategoryElement.setAttribute("id", "");
      hiddenCategoryElement.setAttribute("value", "");
    }

    const enableResponsiveSearchResults =
      this.props.configProps &&
      ConfigPropService.isYesOrTrue(
        this.props.configProps[ConfigProps.EnableResponsiveSearchResults]
      );

    const categoryNameElement = document.getElementById("CategoryName");

    if (categoryNameElement) {
      categoryNameElement.setAttribute("name", "");
      categoryNameElement.setAttribute("id", "");
    }

    const categoryQueryString = enableResponsiveSearchResults
      ? "c"
      : "Category";

    if (
      !this.state.department ||
      this.state.department === AllDepartments.name
    ) {
      // do not submit category if it is blank
    } else {
      // set the category id for the query string
      if (hiddenCategoryElement) {
        hiddenCategoryElement.setAttribute("name", categoryQueryString);
        hiddenCategoryElement.setAttribute("id", categoryQueryString);
        hiddenCategoryElement.setAttribute(
          "value",
          this.state.department.toLowerCase()
        );
      }

      const currentDepartment = this.state.departments.find(
        (department) =>
          this.state.department ===
          (department.searchCategoryId
            ? department.searchCategoryId
            : department.name)
      );

      const categoryNameQueryString = enableResponsiveSearchResults ? "n" : "";

      // set the category name
      if (
        currentDepartment &&
        categoryNameElement &&
        currentDepartment.name !== this.state.department
      ) {
        categoryNameElement.setAttribute("name", categoryNameQueryString);
        categoryNameElement.setAttribute("id", categoryNameQueryString);
        categoryNameElement.setAttribute(
          "value",
          currentDepartment.name.toLowerCase()
        );
      }
      isCategorySearch = true;
    }

    const keywordElement = document.getElementsByClassName(
      this.props.isMobile ? "search-input-mobile" : "search-input"
    )[0] as HTMLInputElement;

    const keywordQueryString = enableResponsiveSearchResults ? "q" : "Keyword";
    keywordElement.setAttribute("name", keywordQueryString);
    keywordElement.setAttribute("id", keywordQueryString);

    // do not submit keyword if it is blank
    if (!search) {
      keywordElement.setAttribute("name", "");
      keywordElement.setAttribute("id", "");
    } else {
      keywordElement.setAttribute("value", keywordElement.value.toLowerCase());
      RecentSearchesService.addRecent(search);
      isKeywordSearch = true;
    }

    // default to keyword search
    let adobeSearchType = "Manual Search";

    if (typeAheadResultSelected) {
      adobeSearchType = "Type Ahead";
    } else if (isCategorySearch && !isKeywordSearch) {
      adobeSearchType = "Category";
    }

    const searchTerm = isKeywordSearch
      ? search
      : isCategorySearch
      ? this.state.department
      : "";

    DigitalDataService.setSearchType(adobeSearchType, searchTerm);

    AnalyticsService.emitCustomEvent("searchPerformed");

    this.onSearchFocus(false);
    const typeAheadKeywordStorageKey = "wbm_typeahead_keyword";
    if (typeAheadResultSelected) {
      sessionStorage.setItem(typeAheadKeywordStorageKey, search);
      this.suggestionMouseLeave();
    } else {
      sessionStorage.removeItem(typeAheadKeywordStorageKey);
    }
  }

  onDepartmentChange(event: ChangeEvent<HTMLSelectElement>) {
    this.setState({
      department: event.target.value,
    });

    if (typeof document !== undefined && event.target.value) {
      const span = document.createElement("span");
      span.style.visibility = "hidden";
      span.style.height = "0px";

      let element = this.state.departments.find(
        (d) => d.searchCategoryId === event.target.value
      );

      if (element == null) {
        element = this.state.departments.find(
          (d) => d.name === event.target.value
        );
      }

      if (element) {
        span.innerText = element.name;

        const htmlElement = document.getElementsByClassName(
          "main-header-container"
        )[0];

        htmlElement.appendChild(span);

        const departmentWidth =
          span.clientWidth || span.offsetWidth || span.scrollWidth;

        const paddingLeft = 10;
        const paddingRight = 36;

        const total = departmentWidth + paddingLeft + paddingRight;

        this.setState({
          departmentSelectWidth: total,
        });

        htmlElement.removeChild(span);
      }
    }
  }

  onSearchClear() {
    this.props.onSearchKeywordChange("");

    const input = document.getElementsByClassName(
      this.props.isMobile ? "search-input-mobile" : "search-input"
    )[0] as HTMLElement;

    input?.focus();

    this.setState({
      hasSearchClearMouse: false,
      hasSearchInput: false,
      hasSearchFocus: true,
    });
  }

  searchClearMouseEnter() {
    this.setState({
      hasSearchClearMouse: true,
    });
  }

  searchClearMouseLeave() {
    this.setState({
      hasSearchClearMouse: false,
    });
  }

  removeRecentSuggestion(value: string) {
    if (
      this.state.suggestions[this.state.suggestions.length - 1].value === value
    ) {
      this.setState({
        deletedLeastRecentSearch: true,
      });
    } else {
      this.setState({
        deletedLeastRecentSearch: false,
      });
    }

    RecentSearchesService.removeRecent(value);

    this.setState({
      suggestions: this.filterSuggestions(
        this.state.suggestions,
        this.props.searchKeyword
      ),
    });
  }

  render() {
    return (
      <div
        className={`search-component ${
          this.props.isMobile ? "mobile no-print" : "desktop"
        }`}
      >
        <form
          role="search"
          method="get"
          acceptCharset="utf-8"
          autoComplete="off"
          action={this.state.shopUrl}
          onSubmit={this.onSubmitHandler}
        >
          {this.props.isMobile ? this.renderMobile() : this.renderDesktop()}
        </form>
      </div>
    );
  }

  renderMobile() {
    return (
      <React.Fragment>
        {this.renderSearchButton()}
        {this.renderSearchInput()}
        {this.state.hasSearchInput && (
          <SearchClear
            onClick={this.onSearchClear}
            onMouseEnter={this.searchClearMouseEnter}
            onMouseLeave={this.searchClearMouseLeave}
            isMobile={true}
          />
        )}
        {this.renderSuggestionDropdown()}
      </React.Fragment>
    );
  }

  renderDesktop() {
    return (
      <React.Fragment>
        <div className="search input-group">
          {this.props.displayState.hasDepartments && (
            <DepartmentSelect
              // className="float-left d-inline-block d-flex input-group-prepend"
              departments={this.state.departments}
              onDepartmentChange={this.onDepartmentChange}
              department={this.state.department}
              width={this.state.departmentSelectWidth}
              displayState={this.props.displayState}
              forceCloseDep={this.props.forceCloseDep}
            />
          )}
          {this.renderSearchInput()}
          {this.renderSearchButton()}
          {this.state.hasSearchInput && (
            <SearchClear
              onClick={this.onSearchClear}
              onMouseEnter={this.searchClearMouseEnter}
              onMouseLeave={this.searchClearMouseLeave}
            />
          )}
          {this.renderSuggestionDropdown()}
          <input type="hidden" id="CategoryName" name="CategoryName"></input>
          <input
            type="hidden"
            id="HiddenCategoryId"
            name="HiddenCategoryId"
          ></input>
        </div>
      </React.Fragment>
    );
  }

  renderSearchButton() {
    return <SearchButton isMobile={this.props.isMobile} />;
  }

  renderSearchInput() {
    return (
      <SearchInput
        onSearchChange={this.onSearchChange}
        onSearchFocus={this.onSearchFocus}
        onSuggestionNavigate={this.onSuggestionNavigate}
        hoveredSuggestion={this.state.hoveredSuggestion}
        suggestions={this.state.suggestions}
        keyword={this.props.searchKeyword}
        offset={
          this.props.displayState.hasDepartments
            ? this.state.departmentSelectWidth
            : 0
        }
        isMobile={this.props.isMobile}
      />
    );
  }

  clickedOutsideOfDropdown() {
    this.setState({
      hasSuggestionMouse: false,
      hasSearchFocus: false,
    });
  }

  renderSuggestionDropdown() {
    return (
      <SuggestionDropdown
        suggestions={this.state.suggestions}
        display={this.state.hasSearchFocus || this.state.hasSuggestionMouse}
        onSelect={this.onSuggestionSelect}
        onRecentRemove={this.removeRecentSuggestion}
        navigationObservable={this.suggestionNavigationObservable}
        onHover={this.onSuggestionHover}
        onMouseEnter={this.suggestionMouseEnter}
        offset={
          this.props.displayState.hasDepartments
            ? this.state.departmentSelectWidth
            : 0
        }
        isMobile={this.props.isMobile}
        clickedOutsideOfDropdown={this.clickedOutsideOfDropdown}
      />
    );
  }
}

export default SearchComponent;
